import React, { useState, useEffect } from "react";
import {
  getOrders, updateOrder,
  getDeliveryStaff, addDeliveryStaff, removeDeliveryStaff,
  getUsers,
} from "../../firebase/firestoreService";
import { useAuth } from "../../context/AuthContext";

const ACTIVE_STATUSES = ["Pending", "Confirmed", "Shipped"];

const STATUS_STYLE = {
  Pending:   { background: "#fff3e0", color: "#f57c00" },
  Confirmed: { background: "#e3f2fd", color: "#1565c0" },
  Shipped:   { background: "#f3e5f5", color: "#7b1fa2" },
};

const fmt = (n) => `MK ${Number(n || 0).toLocaleString()}`;

const fmtAge = (ts) => {
  if (!ts) return "—";
  const d   = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((Date.now() - d) / 60000);
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// ── Orders tab ────────────────────────────────────────────────────────────────
function OrdersTab() {
  const { user } = useAuth();
  const [orders,     setOrders]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [delivering, setDelivering] = useState(null);

  useEffect(() => {
    getOrders().then((all) => {
      setOrders(
        all
          .filter((o) => ACTIVE_STATUSES.includes(o.status || "Pending"))
          .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0))
      );
      setLoading(false);
    });
  }, []);

  const markDelivered = async (id) => {
    setDelivering(id);
    await updateOrder(id, {
      status:          "Delivered",
      deliveredByUid:  user?.uid || "",
      deliveredByName: user?.displayName || user?.phoneNumber || "Admin",
    });
    setOrders((prev) => prev.filter((o) => o.id !== id));
    setDelivering(null);
  };

  const buildWhatsApp = (order) => {
    const phone = (order.customerPhone || "").replace(/\D/g, "");
    const ref   = (order.chargeId || order.id).slice(0, 8).toUpperCase();
    const msg   = `Hi ${order.customerName || "there"}, your Good Vibes Store order #${ref} is on its way! 🚚 We'll be with you shortly.`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  if (loading) return <p className="dash-empty">Loading orders…</p>;
  if (!orders.length) return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "#aaa" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
      <p style={{ fontSize: 14 }}>All orders delivered!</p>
    </div>
  );

  return (
    <div className="delivery-cards">
      {orders.map((order) => {
        const items = (order.items || [])
          .map((i) => `${i.name}${i.selColor ? ` · ${i.selColor}` : ""}${i.selSize ? ` · ${i.selSize}` : ""}${(i.qty || 1) > 1 ? ` ×${i.qty}` : ""}`)
          .join(", ");
        return (
          <div key={order.id} className="delivery-card">
            <div className="delivery-card__top">
              <div className="delivery-card__id">#{(order.chargeId || order.id).slice(0, 8).toUpperCase()}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="delivery-card__age">{fmtAge(order.createdAt)}</span>
                <span className="delivery-card__status" style={STATUS_STYLE[order.status] || STATUS_STYLE.Pending}>
                  {order.status || "Pending"}
                </span>
              </div>
            </div>
            <div className="delivery-card__customer">
              <span className="delivery-card__name">{order.customerName || "—"}</span>
              {order.customerPhone && (
                <a href={buildWhatsApp(order)} target="_blank" rel="noopener noreferrer" className="delivery-card__wa">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {order.customerPhone}
                </a>
              )}
            </div>
            {order.deliverySummary && (
              <div className="delivery-card__address">📍 {order.deliverySummary}</div>
            )}
            <div className="delivery-card__items">{items || "—"}</div>
            <div className="delivery-card__footer">
              <span className="delivery-card__total">{fmt(order.total)}</span>
              <button
                className="delivery-card__done-btn"
                onClick={() => markDelivered(order.id)}
                disabled={delivering === order.id}
              >
                {delivering === order.id ? "Updating…" : "✓ Mark Delivered"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Staff tab ─────────────────────────────────────────────────────────────────
function StaffTab() {
  const [staff,   setStaff]   = useState([]);
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [adding,  setAdding]  = useState(null);

  const load = async () => {
    setLoading(true);
    const [s, u] = await Promise.all([getDeliveryStaff(), getUsers()]);
    setStaff(s);
    setUsers(u);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const staffIds = new Set(staff.map((s) => s.userId));

  const userName = (u) =>
    [u.firstName, u.lastName].filter(Boolean).join(" ") || u.phone || u.email || u.id;

  const filtered = users.filter((u) => {
    if (staffIds.has(u.id)) return false;
    if (!search.trim()) return false;
    const q = search.toLowerCase();
    return (
      userName(u).toLowerCase().includes(q) ||
      (u.phone || "").includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  const handleAdd = async (user) => {
    setAdding(user.id);
    await addDeliveryStaff({ userId: user.id, name: userName(user), phone: user.phone || "" });
    setSearch("");
    await load();
    setAdding(null);
  };

  const handleRemove = async (id) => {
    await removeDeliveryStaff(id);
    await load();
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <p style={{ fontSize: 12, color: "#aaa", marginBottom: 20, lineHeight: 1.6 }}>
        Assigned staff can log in and access the Orders view from their account.
      </p>

      {loading ? (
        <p style={{ fontSize: 13, color: "#aaa" }}>Loading…</p>
      ) : staff.length === 0 ? (
        <p style={{ fontSize: 13, color: "#ccc", marginBottom: 16 }}>No delivery staff assigned yet.</p>
      ) : (
        <div style={{ marginBottom: 24 }}>
          {staff.map((s) => (
            <div key={s.id} className="admins__row">
              <div>
                <span className="admins__email">{s.name || s.userId}</span>
                {s.phone && <span style={{ fontSize: 11, color: "#aaa", marginLeft: 8 }}>{s.phone}</span>}
              </div>
              <button className="btn-danger" onClick={() => handleRemove(s.id)}>Remove</button>
            </div>
          ))}
        </div>
      )}

      <div className="admins__add-row">
        <input
          className="form-input"
          placeholder="Search by name or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {search.trim() && (
        <div style={{ marginTop: 8, border: "1px solid #e0d9d0", borderRadius: 10, overflow: "hidden" }}>
          {filtered.length === 0 ? (
            <p style={{ fontSize: 12, color: "#aaa", padding: "10px 14px" }}>No matching users found.</p>
          ) : (
            filtered.slice(0, 6).map((u) => (
              <div key={u.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", borderBottom: "1px solid #f5f2ee",
              }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{userName(u)}</span>
                  {u.phone && <span style={{ fontSize: 11, color: "#aaa", marginLeft: 8 }}>{u.phone}</span>}
                </div>
                <button
                  className="btn-edit"
                  onClick={() => handleAdd(u)}
                  disabled={adding === u.id}
                >
                  {adding === u.id ? "Adding…" : "Assign"}
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
export default function DeliverySection() {
  const [tab, setTab] = useState("orders");

  return (
    <div>
      <div className="dash-main__header">
        <h2 className="dash-main__title">Delivery</h2>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
          {[{ key: "orders", label: "Orders" }, { key: "staff", label: "Staff" }].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: "5px 16px", fontSize: 11, fontWeight: 600,
                fontFamily: "inherit", borderRadius: 20, border: "1px solid", cursor: "pointer",
                transition: "all .15s",
                borderColor: tab === t.key ? "#1a1a1a" : "#ddd",
                background:  tab === t.key ? "#1a1a1a" : "#fff",
                color:       tab === t.key ? "#fff"    : "#888",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "orders" ? <OrdersTab /> : <StaffTab />}
    </div>
  );
}
